import {
  AffiliationRelations,
  AffiliationTypes,
  EntityTypes,
  MaritalStatus,
} from '@generated/graphql';
import type { Account } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import type { CustodianConnectionAccountTypeSettings } from '@interfaces/custodianConnection';
import { AccountTypes } from '@onevesthq/ov-enums';
import { enumValues } from '@utils/enumValues';
import dayjs from 'dayjs';
import type { TFunction } from 'react-i18next';
import type { FieldOptions } from '../utils';
import { TaxConfig, type TaxConfigResponse } from '../utils/taxConfig';

export enum SectionType {
  AFFILIATE = 'AFFILIATE',
  RRIF_PAYMENT_INSTRUCTIONS = 'RRIF_PAYMENT_INSTRUCTIONS',
  RESP_GRANTS = 'RESP_GRANTS',
  RDSP_GRANTS = 'RDSP_GRANTS',
  JURISDICTION = 'JURISDICTION',
  SOURCE_OF_FUNDS = 'SOURCE_OF_FUNDS',
  LINK_TO_HOUSEHOLD = 'LINK_TO_HOUSEHOLD',
  CUSTODIAN = 'CUSTODIAN',
  BASE_CURRENCY = 'BASE_CURRENCY',
  MULTI_CURRENCY = 'MULTI_CURRENCY',
  DTC_ELIGIBILITY = 'DTC_ELIGIBILITY',
  JOINT_OWNERSHIP_TYPE = 'JOINT_OWNERSHIP_TYPE',
  GROUP_INFORMATION = 'GROUP_INFORMATION',
}

const SPOUSE_AND_COMMON_LAW_RELATIONS = [
  AffiliationRelations.Spouse,
  AffiliationRelations.CommonLaw,
];
const RESP_FAMILY_RELATIONS = [
  AffiliationRelations.Child,
  AffiliationRelations.Grandchild,
  AffiliationRelations.NephewNiece,
  AffiliationRelations.Sibling,
  AffiliationRelations.Other,
];
const RESP_SINGLE_RELATIONS = [...RESP_FAMILY_RELATIONS];
const MINOR_BENEFICIARY_RELATIONS = [
  AffiliationRelations.Child,
  AffiliationRelations.Grandchild,
  AffiliationRelations.NephewNiece,
  AffiliationRelations.Sibling,
  AffiliationRelations.Other,
];
export const ALL_RELATIONS = enumValues(AffiliationRelations);

type FieldOption =
  | 'sourceOfFunds'
  | 'jurisdiction'
  | 'custodianConnection'
  | 'baseCurrency';
type PEPOptions =
  | 'isOfficerOfPublicCompany'
  | 'isOwnerOfPublicCompany'
  | 'isMemberOfIiroc'
  | 'politicallyExposedForeignPerson'
  | 'politicallyExposedDomesticPerson'
  | 'closeAssociateOfPEP'
  | 'headOfInternationalOrganization';
export type FieldTypeOption =
  | 'fullName'
  | 'physicalAddress'
  | 'dateOfBirth'
  | 'dateOfDeath'
  | 'taxId'
  | 'primaryEmail'
  | 'phone'
  | 'gender'
  | 'employmentStatus'
  | 'allocated'
  | 'allocation'
  | 'relation'
  | 'citizenships'
  | 'maritalStatus'
  | 'foreignTaxInformation'
  | PEPOptions
  | 'roleId';

type SectionOption = { label: string; value: string };
export type FieldType = { type?: FieldTypeOption; options?: SectionOption[] };

export type AccountConfigSection = {
  type: SectionType;
  affiliateType?: AffiliationTypes;
  defaultRelationType?: AffiliationRelations;
  multi?: boolean;
  showAllocation?: boolean;
  showRelationship?: boolean;
  excludeProvinces?: string[];
  edittableAfterOpen?: boolean;
  fields?: FieldType[];
  optionalFields?: FieldType[];
  title?: string;
  actionTitle?: string;
  excludeMaritalStatus?: string[];
  useAccountHoldersAddress?: boolean;
  titlePrefix?: string;
  required?: boolean;
  canUseExistingAffiliate?: boolean;
  hidden?: boolean;
};

export type AccountConfigInterface = {
  sections: AccountConfigSection[];
  fieldOptions?: FieldOptions;
  taxConfig?: TaxConfigResponse;
};

export const getAccountConfig = (
  accountData: Partial<Account>,
  options: any,
  t: TFunction,
  respEnhancementEnabled = false,
): AccountConfigInterface => {
  const accountType = accountData?.type;
  const entityType = accountData?.user?.type;
  const custodianAccountTypeSettings =
    accountData?.custodianConnection?.accountTypeSettings ?? [];

  const custodianAccountTypeSettingsMap = custodianAccountTypeSettings.reduce(
    (acc, setting) => {
      acc[setting.type] = setting;
      return acc;
    },
    {} as Record<AccountTypes, CustodianConnectionAccountTypeSettings>,
  );

  switch (accountType) {
    case AccountTypes.CORPORATE_CASH: {
      const sections = [
        corporateCashAuthorizedIndividualAffiliate(
          t,
          options,
          custodianAccountTypeSettingsMap[AccountTypes.CORPORATE_CASH],
        ),
        corporateCashbeneficialOwnerAffiliate(
          t,
          options,
          custodianAccountTypeSettingsMap[AccountTypes.CORPORATE_CASH],
        ),
        corporateCashDirectorAffiliate(
          t,
          options,
          custodianAccountTypeSettingsMap[AccountTypes.CORPORATE_CASH],
        ),
        corporateCashSeniorOfficerAffiliate(
          t,
          options,
          custodianAccountTypeSettingsMap[AccountTypes.CORPORATE_CASH],
        ),
      ];
      if (entityType === EntityTypes.Trust) {
        sections.push(
          corporateCashSettlorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.CORPORATE_CASH],
          ),
        );
      }
      sections.push(
        { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
        { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
        { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
        { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
      );
      return {
        sections,
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    }
    case AccountTypes.PERSONAL:
      return {
        sections: [
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.CASH_JOINT:
      return {
        sections: [
          requiredJointAffiliate(
            t,
            ALL_RELATIONS,
            custodianAccountTypeSettingsMap[AccountTypes.CASH_JOINT],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.JOINT_OWNERSHIP_TYPE, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.MARGIN_JOINT:
      return {
        sections: [
          requiredJointAffiliate(
            t,
            ALL_RELATIONS,
            custodianAccountTypeSettingsMap[AccountTypes.MARGIN_JOINT],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.MARGIN:
      return {
        sections: [
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.INFORMAL_TRUST:
      return {
        sections: [
          informalTrustPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.INFORMAL_TRUST],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
      };
    case AccountTypes.INFORMAL_TRUST_JOINT:
      return {
        sections: [
          requiredJointAffiliate(
            t,
            ALL_RELATIONS,
            custodianAccountTypeSettingsMap[AccountTypes.INFORMAL_TRUST_JOINT],
          ),
          informalTrustPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.INFORMAL_TRUST_JOINT],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
      };
    case AccountTypes.RDSP: {
      const sections = [
        rdspPrimaryBeneficiaryAffiliate(
          t,
          ALL_RELATIONS,
          custodianAccountTypeSettingsMap[AccountTypes.RDSP],
        ),
      ];
      if (beneficiaryAgeIsUnder18(accountData.affiliations)) {
        sections.push(
          rdspPrimaryCaregiverAffiliate(
            respEnhancementEnabled,
            custodianAccountTypeSettingsMap[AccountTypes.RDSP],
          ),
        );
      }
      sections.push(
        { type: SectionType.DTC_ELIGIBILITY },
        { type: SectionType.RDSP_GRANTS },
        { type: SectionType.LINK_TO_HOUSEHOLD },
        { type: SectionType.CUSTODIAN },
        { type: SectionType.BASE_CURRENCY },
        { type: SectionType.MULTI_CURRENCY },
      );
      const fieldOptions = getFieldOptions([
        'custodianConnection',
        'baseCurrency',
      ]);
      return { sections, fieldOptions };
    }
    case AccountTypes.RDSP_JOINT: {
      const sections = [
        rdspPrimaryBeneficiaryAffiliate(
          t,
          ALL_RELATIONS,
          custodianAccountTypeSettingsMap[AccountTypes.RDSP_JOINT],
        ),
      ];
      if (beneficiaryAgeIsUnder18(accountData.affiliations)) {
        sections.push(
          rdspPrimaryCaregiverAffiliate(
            respEnhancementEnabled,
            custodianAccountTypeSettingsMap[AccountTypes.RDSP_JOINT],
          ),
        );
      }
      sections.push(
        requiredJointAffiliate(
          t,
          ALL_RELATIONS,
          custodianAccountTypeSettingsMap[AccountTypes.RDSP_JOINT],
        ),
      );
      sections.push(
        { type: SectionType.DTC_ELIGIBILITY },
        { type: SectionType.RDSP_GRANTS },
        { type: SectionType.LINK_TO_HOUSEHOLD },
        { type: SectionType.CUSTODIAN },
        { type: SectionType.BASE_CURRENCY },
        { type: SectionType.MULTI_CURRENCY },
      );
      const fieldOptions = getFieldOptions([
        'custodianConnection',
        'baseCurrency',
      ]);
      return { sections, fieldOptions };
    }
    case AccountTypes.RRSP:
      return {
        sections: [
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.RRSP],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.TFSA:
      return {
        sections: [
          defaultSuccessorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.TFSA],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.TFSA],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.FHSA:
      return {
        sections: [
          defaultSuccessorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.FHSA],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.FHSA],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.LRSP:
      return {
        sections: [
          defaultOtherAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.LRSP],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.LRSP],
          ),
          { type: SectionType.JURISDICTION, edittableAfterOpen: true },
          { type: SectionType.SOURCE_OF_FUNDS, edittableAfterOpen: true },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions([
          'sourceOfFunds',
          'jurisdiction',
          'custodianConnection',
          'baseCurrency',
        ]),
      };
    case AccountTypes.RLSP:
      return {
        sections: [
          defaultOtherAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.RLSP],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.RLSP],
          ),
          { type: SectionType.JURISDICTION, edittableAfterOpen: true },
          { type: SectionType.SOURCE_OF_FUNDS, edittableAfterOpen: true },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions([
          'sourceOfFunds',
          'jurisdiction',
          'custodianConnection',
          'baseCurrency',
        ]),
      };
    case AccountTypes.LIRA:
      return {
        sections: [
          defaultOtherAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.LIRA],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.LIRA],
          ),
          { type: SectionType.JURISDICTION, edittableAfterOpen: true },
          { type: SectionType.SOURCE_OF_FUNDS, edittableAfterOpen: true },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions([
          'sourceOfFunds',
          'jurisdiction',
          'custodianConnection',
          'baseCurrency',
        ]),
      };
    case AccountTypes.LRIF:
      return {
        sections: [
          {
            ...defaultOtherAffiliate(
              t,
              custodianAccountTypeSettingsMap[AccountTypes.LRIF],
            ),
            required: true,
          },
          rrifPaymentInstructions(t),
          defaultSuccessorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.LRIF],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.LRIF],
          ),
          { type: SectionType.JURISDICTION, edittableAfterOpen: true },
          { type: SectionType.SOURCE_OF_FUNDS, edittableAfterOpen: true },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions([
          'sourceOfFunds',
          'jurisdiction',
          'custodianConnection',
          'baseCurrency',
        ]),
      };
    case AccountTypes.PRIF:
      return {
        sections: [
          {
            ...defaultOtherAffiliate(
              t,
              custodianAccountTypeSettingsMap[AccountTypes.PRIF],
            ),
            required: true,
          },
          rrifPaymentInstructions(t),
          defaultSuccessorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.PRIF],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.PRIF],
          ),
          { type: SectionType.JURISDICTION, edittableAfterOpen: true },
          { type: SectionType.SOURCE_OF_FUNDS, edittableAfterOpen: true },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions([
          'sourceOfFunds',
          'jurisdiction',
          'custodianConnection',
          'baseCurrency',
        ]),
      };
    case AccountTypes.LIF:
      return {
        sections: [
          {
            ...defaultOtherAffiliate(
              t,
              custodianAccountTypeSettingsMap[AccountTypes.LIF],
            ),
            required: true,
          },
          rrifPaymentInstructions(t),
          defaultSuccessorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.LIF],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.LIF],
          ),
          { type: SectionType.JURISDICTION, edittableAfterOpen: true },
          { type: SectionType.SOURCE_OF_FUNDS, edittableAfterOpen: true },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions([
          'sourceOfFunds',
          'jurisdiction',
          'custodianConnection',
          'baseCurrency',
        ]),
      };
    case AccountTypes.RLIF:
      return {
        sections: [
          {
            ...defaultOtherAffiliate(
              t,
              custodianAccountTypeSettingsMap[AccountTypes.RLIF],
            ),
            required: true,
          },
          {
            ...rrifPaymentInstructions(t),
            titlePrefix: t('paymentInstruction:lifTitle'),
          },
          defaultSuccessorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.RLIF],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.RLIF],
          ),
          { type: SectionType.JURISDICTION, edittableAfterOpen: true },
          { type: SectionType.SOURCE_OF_FUNDS, edittableAfterOpen: true },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions([
          'sourceOfFunds',
          'jurisdiction',
          'custodianConnection',
          'baseCurrency',
        ]),
      };
    case AccountTypes.RRIF:
      return {
        sections: [
          {
            ...defaultOtherAffiliate(
              t,
              custodianAccountTypeSettingsMap[AccountTypes.RRIF],
            ),
            required: true,
          },
          rrifPaymentInstructions(t),
          defaultSuccessorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.RRIF],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.RRIF],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.RRSP_SPOUSAL:
      return {
        sections: [
          defaultContributorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.RRSP_SPOUSAL],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.RRSP_SPOUSAL],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.RIF_SPOUSAL:
      return {
        sections: [
          defaultContributorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.RIF_SPOUSAL],
          ),
          rrifPaymentInstructions(t),
          defaultSuccessorAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.RIF_SPOUSAL],
          ),
          defaultPrimaryBeneficiaryAffiliate(
            custodianAccountTypeSettingsMap[AccountTypes.RIF_SPOUSAL],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.RESP_FAMILY_JOINT:
      return {
        sections: [
          requiredJointAffiliate(
            t,
            SPOUSE_AND_COMMON_LAW_RELATIONS,
            custodianAccountTypeSettingsMap[AccountTypes.RESP_FAMILY_JOINT],
          ),
          respGuardianAffiliate(
            respEnhancementEnabled,
            t,
            { canUseExistingAffiliate: true },
            custodianAccountTypeSettingsMap[AccountTypes.RESP_FAMILY_JOINT],
          ),
          {
            ...respPrimaryCaregiverAffiliate(
              respEnhancementEnabled,
              t,
              { canUseExistingAffiliate: true },
              custodianAccountTypeSettingsMap[AccountTypes.RESP_FAMILY_JOINT],
            ),
            multi: true,
          },
          {
            ...respPrimaryBeneficiaryAffiliate(
              t,
              RESP_FAMILY_RELATIONS,
              custodianAccountTypeSettingsMap[AccountTypes.RESP_FAMILY_JOINT],
            ),
            multi: true,
          },
          { type: SectionType.RESP_GRANTS, edittableAfterOpen: false },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.RESP_SINGLE_JOINT:
      return {
        sections: [
          requiredJointAffiliate(
            t,
            SPOUSE_AND_COMMON_LAW_RELATIONS,
            custodianAccountTypeSettingsMap[AccountTypes.RESP_SINGLE_JOINT],
          ),
          {
            ...respGuardianAffiliate(
              respEnhancementEnabled,
              t,
              { canUseExistingAffiliate: true },
              custodianAccountTypeSettingsMap[AccountTypes.RESP_SINGLE_JOINT],
            ),
            multi: false,
          },
          respPrimaryCaregiverAffiliate(
            respEnhancementEnabled,
            t,
            { canUseExistingAffiliate: true },
            custodianAccountTypeSettingsMap[AccountTypes.RESP_SINGLE_JOINT],
          ),
          respPrimaryBeneficiaryAffiliate(
            t,
            RESP_SINGLE_RELATIONS,
            custodianAccountTypeSettingsMap[AccountTypes.RESP_SINGLE_JOINT],
          ),
          { type: SectionType.RESP_GRANTS, edittableAfterOpen: false },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.RESP_FAMILY:
      return {
        sections: [
          respGuardianAffiliate(
            respEnhancementEnabled,
            t,
            {},
            custodianAccountTypeSettingsMap[AccountTypes.RESP_FAMILY],
          ),
          {
            ...respPrimaryCaregiverAffiliate(
              respEnhancementEnabled,
              t,
              {},
              custodianAccountTypeSettingsMap[AccountTypes.RESP_FAMILY],
            ),
            multi: true,
          },
          {
            ...respPrimaryBeneficiaryAffiliate(
              t,
              RESP_FAMILY_RELATIONS,
              custodianAccountTypeSettingsMap[AccountTypes.RESP_FAMILY],
            ),
            multi: true,
          },
          { type: SectionType.RESP_GRANTS, edittableAfterOpen: false },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.RESP_SINGLE:
      return {
        sections: [
          {
            ...respGuardianAffiliate(
              respEnhancementEnabled,
              t,
              {},
              custodianAccountTypeSettingsMap[AccountTypes.RESP_SINGLE],
            ),
            multi: false,
          },
          respPrimaryCaregiverAffiliate(
            respEnhancementEnabled,
            t,
            {},
            custodianAccountTypeSettingsMap[AccountTypes.RESP_SINGLE],
          ),
          respPrimaryBeneficiaryAffiliate(
            t,
            RESP_SINGLE_RELATIONS,
            custodianAccountTypeSettingsMap[AccountTypes.RESP_SINGLE],
          ),
          { type: SectionType.RESP_GRANTS, edittableAfterOpen: false },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.RESP_ADULT:
      return {
        sections: [
          respAdultBeneficiaryAffiliate(
            t,
            undefined,
            custodianAccountTypeSettingsMap[AccountTypes.RESP_ADULT],
          ),
          { type: SectionType.RESP_GRANTS, edittableAfterOpen: false },
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.USA_BROKERAGE:
      return {
        sections: [
          usaPrimaryBeneficiaryAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.USA_BROKERAGE],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.USA_IRA:
      return {
        sections: [
          usaPrimaryBeneficiaryAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.USA_IRA],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        taxConfig: TaxConfig({
          accountType: AccountTypes.USA_IRA,
        }),
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.USA_RO_IRA:
      return {
        sections: [
          usaPrimaryBeneficiaryAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.USA_RO_IRA],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
        taxConfig: TaxConfig({
          accountType: AccountTypes.USA_RO_IRA,
        }),
      };
    case AccountTypes.USA_RT_IRA:
      return {
        sections: [
          usaPrimaryBeneficiaryAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.USA_RT_IRA],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
          { type: SectionType.CUSTODIAN, edittableAfterOpen: true },
          { type: SectionType.BASE_CURRENCY, edittableAfterOpen: true },
          { type: SectionType.MULTI_CURRENCY, edittableAfterOpen: true },
        ],
        taxConfig: TaxConfig({
          accountType: AccountTypes.USA_RT_IRA,
        }),
        fieldOptions: getFieldOptions(['custodianConnection', 'baseCurrency']),
      };
    case AccountTypes.USA_ESA:
      return {
        sections: [
          usaMinorBeneficiaryAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.USA_ESA],
          ),
          { type: SectionType.LINK_TO_HOUSEHOLD, edittableAfterOpen: true },
        ],
      };
    case AccountTypes.USA_INH_IRA:
      return {
        sections: [
          usaDecendantAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.USA_INH_IRA],
          ),
        ],
      };
    case AccountTypes.USA_INH_RT_IRA:
      return {
        sections: [
          usaDecendantAffiliate(
            t,
            custodianAccountTypeSettingsMap[AccountTypes.USA_INH_RT_IRA],
          ),
        ],
      };
    case AccountTypes.GRSP:
      return {
        sections: [
          { type: SectionType.GROUP_INFORMATION, edittableAfterOpen: true },
        ],
      };
    default:
      return {
        sections: [],
      };
  }
};

const getFieldOptions = (options: FieldOption[]) =>
  options.reduce<Record<string, { required: true }>>((acc, option) => {
    acc[option] = { required: true };
    return acc;
  }, {});

const defaultPrimaryBeneficiaryAffiliate = (
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) =>
        setting.affiliateType === AffiliationTypes.PrimaryBeneficiary,
    );

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'relation',
    'allocation',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.PrimaryBeneficiary,
    multi: true,
    showAllocation: true,
    showRelationship: true,
    excludeProvinces: ['QC'],
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
    })),
  };
};

const informalTrustPrimaryBeneficiaryAffiliate = (
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) =>
        setting.affiliateType === AffiliationTypes.PrimaryBeneficiary,
    );

  const defaultFields = ['fullName', 'relation', 'taxId'] as FieldTypeOption[];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.PrimaryBeneficiary,
    required: true,
    multi: false,
    showAllocation: false,
    showRelationship: true,
    edittableAfterOpen: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
    })),
  };
};

const requiredJointAffiliate = (
  t: TFunction,
  relations: AffiliationRelations[],
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => ({
  ...jointAffiliate(t, relations, custodianAccountTypeSettings),
  required: true,
});

const defaultOtherAffiliate = (
  t: TFunction,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Other,
    );
  const relationOptions = SPOUSE_AND_COMMON_LAW_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'dateOfBirth',
    'primaryEmail',
    'taxId',
    'employmentStatus',
    'relation',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.Other,
    multi: false,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: false,
    useAccountHoldersAddress: true,
    title: t('affiliationTypes:SPOUSE_OR_COMMON_LAW_PARTNER'),
    actionTitle: t('affiliationTypes:button.SPOUSE_OR_COMMON_LAW_PARTNER'),
    excludeMaritalStatus: [
      MaritalStatus.Single,
      MaritalStatus.Divorced,
      MaritalStatus.Separated,
      MaritalStatus.Widowed,
    ],
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const defaultContributorAffiliate = (
  t: TFunction,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Contributor,
    );
  const relationOptions = SPOUSE_AND_COMMON_LAW_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'dateOfBirth',
    'primaryEmail',
    'taxId',
    'employmentStatus',
    'relation',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.Contributor,
    multi: false,
    required: true,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: false,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const defaultSuccessorAffiliate = (
  t: TFunction,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Successor,
    );
  const relationOptions = SPOUSE_AND_COMMON_LAW_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'relation',
  ];

  const defaultOptionalFields: FieldTypeOption[] = ['taxId'];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.Successor,
    multi: false,
    showAllocation: false,
    showRelationship: false,
    excludeProvinces: ['QC'],
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (
      custodianSettings?.optionalFields || defaultOptionalFields
    ).map((type) => ({ type, options: fieldOptions[type] })),
  };
};

const rrifPaymentInstructions = (t: TFunction): AccountConfigSection => ({
  type: SectionType.RRIF_PAYMENT_INSTRUCTIONS,
  edittableAfterOpen: true,
  titlePrefix: t('paymentInstruction:rifTitle'),
});

const respPrimaryBeneficiaryAffiliate = (
  t: TFunction,
  relations?: AffiliationRelations[],
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) =>
        setting.affiliateType === AffiliationTypes.PrimaryBeneficiary,
    );
  const relationOptions = relations?.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'dateOfBirth',
    'taxId',
    'relation',
    'allocation',
  ];

  const defaultOptionalFields: FieldTypeOption[] = ['gender'];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.PrimaryBeneficiary,
    multi: false,
    required: true,
    showAllocation: true,
    showRelationship: true,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (
      custodianSettings?.optionalFields || defaultOptionalFields
    ).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const respAdultBeneficiaryAffiliate: typeof respPrimaryBeneficiaryAffiliate = (
  ...args
) => {
  const baseAffiliate = respPrimaryBeneficiaryAffiliate(...args);
  const [t, relations, custodianAccountTypeSettings] = args;
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) =>
        setting.affiliateType === AffiliationTypes.PrimaryBeneficiary,
    );
  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'dateOfBirth',
    'taxId',
  ];
  const defaultOptionalFields: FieldTypeOption[] = ['gender'];

  const relationOptions = relations?.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };
  return {
    ...baseAffiliate,
    edittableAfterOpen: false,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (
      custodianSettings?.optionalFields || defaultOptionalFields
    ).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const respGuardianAffiliate = (
  respEnhancementEnabled: boolean,
  _t: TFunction,
  {
    canUseExistingAffiliate = false,
  }: { canUseExistingAffiliate?: boolean } = {},
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Guardian,
    ) ??
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Other,
    );

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'primaryEmail',
    ...(respEnhancementEnabled ? ['relation' as const] : []),
  ];

  return {
    type: SectionType.AFFILIATE,
    ...(respEnhancementEnabled
      ? {
          affiliateType: AffiliationTypes.Guardian,
        }
      : {
          affiliateType: AffiliationTypes.Other,
          defaultRelationType: AffiliationRelations.Guardian,
        }),
    canUseExistingAffiliate,
    multi: true,
    required: true,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
    })),
  };
};

const respPrimaryCaregiverAffiliate = (
  respEnhancementEnabled: boolean,
  _t: TFunction,
  {
    canUseExistingAffiliate = false,
  }: { canUseExistingAffiliate?: boolean } = {},
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.PrimaryCaregiver,
    ) ??
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Other,
    );

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'primaryEmail',
    'taxId',
    ...(respEnhancementEnabled ? ['relation' as const] : []),
  ];

  return {
    type: SectionType.AFFILIATE,
    ...(respEnhancementEnabled
      ? {
          affiliateType: AffiliationTypes.PrimaryCaregiver,
        }
      : {
          affiliateType: AffiliationTypes.Other,
          defaultRelationType: AffiliationRelations.PrimaryCaregiver,
        }),
    canUseExistingAffiliate,
    multi: false,
    required: true,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
    })),
  };
};

const rdspPrimaryBeneficiaryAffiliate = (
  t: TFunction,
  relations?: AffiliationRelations[],
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) =>
        setting.affiliateType === AffiliationTypes.PrimaryBeneficiary,
    );
  const relationOptions = relations?.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'dateOfBirth',
    'taxId',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.PrimaryBeneficiary,
    multi: false,
    required: true,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const rdspPrimaryCaregiverAffiliate = (
  respEnhancementEnabled = false,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Other,
    );

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'primaryEmail',
    'taxId',
  ];

  return {
    type: SectionType.AFFILIATE,
    ...(respEnhancementEnabled
      ? {
          affiliateType: AffiliationTypes.PrimaryCaregiver,
        }
      : {
          affiliateType: AffiliationTypes.Other,
          defaultRelationType: AffiliationRelations.PrimaryCaregiver,
        }),
    canUseExistingAffiliate: true,
    multi: false,
    required: true,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
    })),
  };
};

const beneficiaryAgeIsUnder18 = (affiliations?: Affiliation[]) => {
  if (!affiliations) return false;
  const beneficiary = affiliations?.find(
    (aff: Affiliation) => aff.type === AffiliationTypes.PrimaryBeneficiary,
  );
  return (
    beneficiary?.user?.dateOfBirth &&
    dayjs().diff(dayjs(beneficiary.user.dateOfBirth), 'years') < 18
  );
};

const jointAffiliate = (
  t: TFunction,
  relations: AffiliationRelations[],
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Joint,
    );
  const relationOptions = relations.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'dateOfBirth',
    'primaryEmail',
    'relation',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.Joint,
    multi: false,
    required: false,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const usaPrimaryBeneficiaryAffiliate = (
  t: TFunction,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) =>
        setting.affiliateType === AffiliationTypes.PrimaryBeneficiary,
    );
  const relationOptions = ALL_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'dateOfBirth',
    'allocated',
    'relation',
  ];

  const defaultOptionalFields: FieldTypeOption[] = ['taxId', 'primaryEmail'];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.PrimaryBeneficiary,
    multi: false,
    showAllocation: true,
    showRelationship: true,
    edittableAfterOpen: true,
    useAccountHoldersAddress: false,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (
      custodianSettings?.optionalFields || defaultOptionalFields
    ).map((type) => ({ type, options: fieldOptions[type] })),
  };
};

const usaMinorBeneficiaryAffiliate = (
  t: TFunction,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) =>
        setting.affiliateType === AffiliationTypes.PrimaryBeneficiary,
    );
  const relationOptions = MINOR_BENEFICIARY_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'dateOfBirth',
    'taxId',
    'relation',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.PrimaryBeneficiary,
    multi: false,
    required: true,
    showAllocation: false,
    showRelationship: true,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const corporateCashbeneficialOwnerAffiliate = (
  t: TFunction,
  options?: any,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.BeneficialOwner,
    );
  const relationOptions = ALL_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'primaryEmail',
    'allocated',
    'taxId',
    'dateOfBirth',
    'employmentStatus',
    'citizenships',
    'isOfficerOfPublicCompany',
    'isOwnerOfPublicCompany',
    'isMemberOfIiroc',
    'politicallyExposedForeignPerson',
    'politicallyExposedDomesticPerson',
    'closeAssociateOfPEP',
    'headOfInternationalOrganization',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.BeneficialOwner,
    defaultRelationType: AffiliationRelations.Other,
    multi: !options?.disableAddingNewAffiliates || false,
    showAllocation: true,
    showRelationship: true,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const corporateCashAuthorizedIndividualAffiliate = (
  t: TFunction,
  options?: any,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) =>
        setting.affiliateType === AffiliationTypes.AuthorizedIndividual,
    );
  const relationOptions = ALL_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'primaryEmail',
    'dateOfBirth',
    'taxId',
    'employmentStatus',
    'maritalStatus',
    'citizenships',
    'foreignTaxInformation',
    'isOfficerOfPublicCompany',
    'isOwnerOfPublicCompany',
    'isMemberOfIiroc',
    'politicallyExposedForeignPerson',
    'politicallyExposedDomesticPerson',
    'closeAssociateOfPEP',
    'headOfInternationalOrganization',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.AuthorizedIndividual,
    defaultRelationType: AffiliationRelations.Other,
    multi: !options?.disableAddingNewAffiliates || false,
    showAllocation: false,
    showRelationship: true,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const corporateCashDirectorAffiliate = (
  t: TFunction,
  options?: any,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Director,
    );
  const relationOptions = ALL_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'primaryEmail',
    'dateOfBirth',
    'employmentStatus',
    'taxId',
    'citizenships',
    'isOfficerOfPublicCompany',
    'isOwnerOfPublicCompany',
    'isMemberOfIiroc',
    'politicallyExposedForeignPerson',
    'politicallyExposedDomesticPerson',
    'closeAssociateOfPEP',
    'headOfInternationalOrganization',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.Director,
    defaultRelationType: AffiliationRelations.Other,
    multi: !options?.disableAddingNewAffiliates || false,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const corporateCashSeniorOfficerAffiliate = (
  t: TFunction,
  options?: any,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.SeniorOfficer,
    );
  const relationOptions = ALL_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'physicalAddress',
    'primaryEmail',
    'dateOfBirth',
    'employmentStatus',
    'taxId',
    'citizenships',
    'isOfficerOfPublicCompany',
    'isOwnerOfPublicCompany',
    'isMemberOfIiroc',
    'politicallyExposedForeignPerson',
    'politicallyExposedDomesticPerson',
    'closeAssociateOfPEP',
    'headOfInternationalOrganization',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.SeniorOfficer,
    defaultRelationType: AffiliationRelations.Other,
    multi: !options?.disableAddingNewAffiliates || false,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const corporateCashSettlorAffiliate = (
  t: TFunction,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Settlor,
    );
  const relationOptions = ALL_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields = [
    'fullName',
    'physicalAddress',
    'primaryEmail',
    'dateOfBirth',
    'employmentStatus',
    'taxId',
    'citizenships',
    'isOfficerOfPublicCompany',
    'isOwnerOfPublicCompany',
    'isMemberOfIiroc',
    'politicallyExposedForeignPerson',
    'politicallyExposedDomesticPerson',
    'closeAssociateOfPEP',
    'headOfInternationalOrganization',
  ] as const;

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.Settlor,
    defaultRelationType: AffiliationRelations.Other,
    multi: false,
    showAllocation: false,
    showRelationship: false,
    edittableAfterOpen: true,
    useAccountHoldersAddress: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};

const usaDecendantAffiliate = (
  t: TFunction,
  custodianAccountTypeSettings?: CustodianConnectionAccountTypeSettings,
): AccountConfigSection => {
  const custodianSettings =
    custodianAccountTypeSettings?.affiliateSettings?.find(
      (setting) => setting.affiliateType === AffiliationTypes.Decedent,
    );
  const relationOptions = ALL_RELATIONS.map((value) => ({
    label: t(`affiliationTypes:affiliationRelationship.${value}`),
    value,
  }));
  const fieldOptions: Record<string, SectionOption[] | undefined> = {
    relation: relationOptions,
  };

  const defaultFields: FieldTypeOption[] = [
    'fullName',
    'dateOfBirth',
    'dateOfDeath',
    'taxId',
    'relation',
  ];

  return {
    type: SectionType.AFFILIATE,
    affiliateType: AffiliationTypes.Decedent,
    multi: false,
    showAllocation: false,
    showRelationship: true,
    edittableAfterOpen: false,
    required: true,
    fields: (custodianSettings?.requiredFields || defaultFields).map(
      (type) => ({ type, options: fieldOptions[type] }),
    ),
    optionalFields: (custodianSettings?.optionalFields || []).map((type) => ({
      type,
      options: fieldOptions[type],
    })),
  };
};
